/**
 * Auto-translate messages via translateMessage Cloud Function (Claude Haiku).
 * Traduit chaque message dont la langue diffère de celle de l'utilisateur.
 * Cache in-memory par messageId pour éviter les appels répétés.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { translateMessage } from '@/services/firebaseFunctions';
import { useLanguage } from '@/contexts/LanguageContext';

interface Entry {
  original: string;
  translated: string;
  sourceLang: string;
  targetLang: string;
}

const cache = new Map<string, Entry>();

/** Détection très légère de la langue (fr vs en) */
function guessLang(text: string): 'fr' | 'en' {
  const t = text.toLowerCase();
  const frMarkers = /\b(le |la |les |un |une |des |bonjour|merci|est |avec |pour |vous |nous |mais |oui |non |très|à |où|ça|ê|ô|œ|ç|é|è|ê|à)\b|[éèêàâôûîçœ]/;
  return frMarkers.test(t) ? 'fr' : 'en';
}

export function useMessageTranslation(
  messages: Array<{ id: string; content: string } & Record<string, any>>
) {
  const { language } = useLanguage();
  const target = language === 'fr' ? 'fr' : 'en';
  const [, force] = useState(0);
  const inflight = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const msg of messages || []) {
        if (!msg?.id || !msg.content) continue;
        const source = guessLang(msg.content);
        if (source === target) continue;
        const key = `${msg.id}:${target}`;
        if (cache.has(key) || inflight.current.has(key)) continue;
        inflight.current.add(key);
        try {
          const res = await translateMessage({
            text: msg.content,
            sourceLang: source,
            targetLang: target,
          });
          if (cancelled) return;
          cache.set(key, {
            original: msg.content,
            translated: res.translation || msg.content,
            sourceLang: source,
            targetLang: target,
          });
          force((n) => n + 1);
        } catch (err) {
          console.warn('[useMessageTranslation] failed for', msg.id, err);
        } finally {
          inflight.current.delete(key);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, target]);

  const getDisplay = useCallback(
    (msg: { id: string; content: string }) => {
      if (!msg?.content) return { text: '', translated: false, original: '' };
      const key = `${msg.id}:${target}`;
      const entry = cache.get(key);
      if (entry) {
        return { text: entry.translated, translated: true, original: entry.original };
      }
      return { text: msg.content, translated: false, original: msg.content };
    },
    [target]
  );

  return { getDisplay };
}
